/**
 * CalibrationStatusCard — shows calibration state in plain language.
 *
 * Design rules:
 * - NEVER show σ / k values — only coach_messages and gate_outcomes_brief.
 * - Degraded-mode messages per T6_DESIGN.md §5:
 *   - 0-1 good takes: "Add at least 2 correct example videos..."
 *   - exactly 2 good takes: same-person warning banner.
 *   - per-dimension view-limited outcomes: plain-language list.
 *   - take failed validation: shown on the TakeRow, not here.
 *
 * Single-voice rendering (red-team finding F5, coach-feedback-for-example-
 * videos): CoachFeedbackList (rendered by the parent, above this card) is
 * now the primary voice for "what your example videos taught the scoring
 * system". Two things here defer to it rather than duplicating it:
 *   1. The legacy `coach_messages` list is collapsed behind a "Details"
 *      disclosure instead of always-inline — it still exists (it can carry
 *      messages coach_feedback doesn't), just isn't in the primary flow.
 *   2. The hardcoded n=2 "generous tolerances" Alert below is suppressed
 *      whenever `coach_feedback` already contains an action item covering
 *      that same topic — see `coversSamePersonTopic` for the exact
 *      detection rule and its ASSUMPTION note.
 *
 * "Re-measure takes" (backlog: recompile-button-remeasure-gap.md): a real UI
 * trigger for take re-measurement, adjacent to GradeScaleStatusRow — the
 * "Recompile" button previously only ran the LLM rule compiler, leaving no
 * product-visible way to recover from a stranded-measurement state after a
 * SCORING_MODEL_VERSION deploy.
 */

import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { Alert, CollapsibleSection, Spinner } from '@/components/ui';
import { cn } from '@/utils/cn';
import { useRecalibrateTakes } from '@/hooks/useCalibrationTakes';
import type { CalibrationSummary } from '@/types/calibrationTake';

interface CalibrationStatusCardProps {
  templateId: string;
  summary: CalibrationSummary | null;
  /** Total completed good takes, used to infer degraded-mode messaging. */
  nCompletedGood: number;
}

/**
 * "Re-measure takes" button — re-runs measurement of every example take
 * under the current scoring version (backlog: recompile-button-remeasure-gap.md).
 * Rendered adjacent to GradeScaleStatusRow, inside the status header card.
 */
function RecalibrateTakesButton({
  templateId,
  summary,
}: {
  templateId: string;
  summary: CalibrationSummary;
}) {
  const { trigger, isRecalibrating, isTriggering, triggerError } =
    useRecalibrateTakes(templateId);

  const busy = isRecalibrating || isTriggering;

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => void trigger(summary.computed_at ?? null)}
        disabled={busy}
        title="Re-runs measurement of all example takes under the current scoring version. Needed after platform scoring updates."
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium
          border border-border-subtle text-text-secondary hover:bg-dark-elevated-3
          transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {busy && <Spinner size="sm" />}
        {busy ? 'Re-measuring…' : 'Re-measure takes'}
      </button>
      {triggerError && (
        <span className="text-xs text-red-400">
          Could not start re-measurement — please try again.
        </span>
      )}
    </div>
  );
}

/**
 * True when `coach_feedback` already carries an action item about the same
 * "only N correct takes / generous tolerances" concern the hardcoded
 * same_person_warning Alert exists for — in which case that Alert would be
 * a second voice saying the same thing and should be suppressed (F5).
 *
 * ASSUMPTION flagged for backend contract confirmation: detects this via
 * `dimension_key === 'same_person_warning'`, reusing the exact field name
 * this Alert is itself gated on (`CalibrationSummary.same_person_warning`)
 * as the natural, self-documenting key for a backend author deliberately
 * targeting this suppression case. No such key is confirmed on the backend
 * yet — verify against the actual coach_feedback dimension_key vocabulary
 * once the parallel backend agent lands it, and adjust this predicate if
 * it differs.
 */
function coversSamePersonTopic(summary: CalibrationSummary): boolean {
  return (summary.coach_feedback ?? []).some(
    (item) => item.severity === 'action' && item.dimension_key === 'same_person_warning',
  );
}

/**
 * Measurement-freshness row (backlog: calibration-auto-rescore-sweep-on-
 * stale-detection.md, Task 3; Batch 2 red-team Finding 3). Distinguishes the
 * three states a coach previously could not tell apart from this card alone:
 *   - "re-measuring now" — a recalibration run (auto-triggered, or from an
 *     edit/explicit click) is actually in flight.
 *   - "needs re-measurement, retrying automatically" — takes have gone
 *     stale (e.g. after a scoring update) but nothing is running yet; the
 *     backend auto-sweep will start (or retry) one shortly on its own
 *     (`takes_stale` + `recalibration_in_progress` on `CalibrationSummary`).
 *   - "needs re-measurement, ask your coach/admin" — the backend retried
 *     recalibrating this template's current stale fingerprint up to its
 *     bound and none of those attempts cleared the staleness
 *     (`recalibration_retries_exhausted`); it will NOT try again on its own
 *     for this fingerprint, so telling the coach "we'll start this
 *     automatically" would be false.
 * Renders nothing when `takes_stale` and `recalibration_in_progress` are
 * both absent/false — the common case.
 */
function MeasurementFreshnessRow({ summary }: { summary: CalibrationSummary }) {
  if (summary.recalibration_in_progress) {
    return (
      <div
        data-testid="measurement-freshness-in-progress"
        className="flex items-center gap-1.5 text-xs text-text-secondary"
      >
        <Spinner size="sm" />
        <span>Re-measuring your example videos now…</span>
      </div>
    );
  }
  if (summary.takes_stale) {
    if (summary.recalibration_retries_exhausted) {
      return (
        <div
          data-testid="measurement-freshness-retries-exhausted"
          className="flex items-start gap-1.5"
        >
          <ExclamationTriangleIcon
            className="h-4 w-4 flex-shrink-0 mt-0.5 text-warning"
            aria-hidden="true"
          />
          <p className="text-xs text-text-secondary">
            Needs re-measurement — this did not finish automatically. Ask your coach or
            admin to run it.
          </p>
        </div>
      );
    }
    return (
      <div
        data-testid="measurement-freshness-stale"
        className="flex items-start gap-1.5"
      >
        <ExclamationTriangleIcon
          className="h-4 w-4 flex-shrink-0 mt-0.5 text-warning"
          aria-hidden="true"
        />
        <p className="text-xs text-text-secondary">
          Needs re-measurement — we&rsquo;ll start this automatically. You don&rsquo;t need
          to do anything.
        </p>
      </div>
    );
  }
  return null;
}

/**
 * Grade-scale status row (grade-scale-status-surface backlog item). Placed
 * above the "Details" disclosure, inside the same status-header card.
 *
 * Before this existed, an ACTIVE grade scale surfaced as nothing at all,
 * and a REFUSED one only ever showed up as one prose item inside
 * CoachFeedbackList — itself vulnerable to that component's action-item
 * display cap (see backend TestGradeScaleRefusalSurvivesActionCap). A coach
 * who just fixed one anchor inversion had no direct way to tell whether the
 * scale was now active or quietly refused on a different pair.
 *
 * Renders nothing when `grade_scale_status` is absent — the backend only
 * omits it when no grade_scale block has ever been minted for this
 * template (legacy calibration, or a mint attempt that never completed),
 * which in practice means the coach hasn't graded any example videos yet.
 * `coach_message` is rendered verbatim (never re-derived) — it is the exact
 * same fact-copy `coach_feedback` uses for this status/refusal_reason.
 */
function GradeScaleStatusRow({ summary }: { summary: CalibrationSummary }) {
  const gradeScale = summary.grade_scale_status;
  if (!gradeScale) {
    return null;
  }

  const isActive = gradeScale.status === 'active';
  const anchorsUsed = gradeScale.anchors_used ?? 0;
  const mergedPairs = gradeScale.merged_pairs ?? 0;

  return (
    <div data-testid="grade-scale-status" className="flex items-start gap-1.5">
      {isActive ? (
        <CheckCircleIcon
          className="h-4 w-4 flex-shrink-0 mt-0.5 text-success"
          aria-hidden="true"
        />
      ) : (
        <ExclamationTriangleIcon
          className="h-4 w-4 flex-shrink-0 mt-0.5 text-warning"
          aria-hidden="true"
        />
      )}
      <div className="space-y-0.5">
        <p className={cn('text-sm font-medium', isActive ? 'text-success' : 'text-warning')}>
          {isActive
            ? `Grading scale: Active (${anchorsUsed} anchor${anchorsUsed === 1 ? '' : 's'})`
            : `Grading scale: Not set — ${gradeScale.coach_message ?? 'not enough data yet.'}`}
        </p>
        {mergedPairs > 0 && (
          <p className="text-xs text-text-muted" data-testid="grade-scale-merged-note">
            {mergedPairs} grade{mergedPairs === 1 ? '' : 's'} treated as tied — measured too
            close to distinguish.
          </p>
        )}
      </div>
    </div>
  );
}

export default function CalibrationStatusCard({
  templateId,
  summary,
  nCompletedGood,
}: CalibrationStatusCardProps) {
  // ── Degraded mode: not enough takes ──────────────────────────────────────
  if (nCompletedGood < 2) {
    return (
      <Alert variant="info">
        Add at least 2 correct example videos to enable calibrated scoring. Scores will
        use the built-in motion prior until calibration is available.
      </Alert>
    );
  }

  // ── Calibration pending (takes uploaded but worker hasn't finished) ───────
  if (!summary) {
    return (
      <div className="rounded-lg border border-border-subtle bg-dark-elevated-2 p-4 space-y-2">
        <p className="text-sm font-semibold text-text-primary">Calibration pending</p>
        <p className="text-sm text-text-secondary">
          Reference takes are processing. Calibration will update automatically when they
          finish.
        </p>
      </div>
    );
  }

  const isCalibrationActive = summary.n_phases_calibrated > 0;

  return (
    <div className="space-y-3">
      {/* Status header */}
      <div className="rounded-lg border border-border-subtle bg-dark-elevated-2 p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-sm font-semibold text-text-primary">
              {isCalibrationActive ? 'Calibration active' : 'Calibration pending'}
            </p>
            {summary.computed_at && (
              <p className="text-xs text-text-muted mt-0.5">
                Last updated{' '}
                {new Date(summary.computed_at).toLocaleString(undefined, {
                  dateStyle: 'medium',
                  timeStyle: 'short',
                })}
              </p>
            )}
          </div>
          <div className="flex gap-2 flex-shrink-0 text-xs text-text-secondary">
            <span>{summary.n_good_takes} correct</span>
            {summary.n_bad_takes > 0 && <span>{summary.n_bad_takes} flawed</span>}
            {(summary.n_fail_takes ?? 0) > 0 && (
              <span>{summary.n_fail_takes} disqualifying</span>
            )}
          </div>
        </div>

        {/* Measurement-freshness row (calibration-auto-rescore-sweep-on-
            stale-detection.md Task 3) — above grade-scale status, since it
            can explain WHY a grade-scale status is about to change. */}
        <MeasurementFreshnessRow summary={summary} />

        {/* Grade-scale status (grade-scale-status-surface backlog item) —
            above the "Details" disclosure, see GradeScaleStatusRow. */}
        <GradeScaleStatusRow summary={summary} />

        {/* "Re-measure takes" (backlog: recompile-button-remeasure-gap.md) —
            adjacent to the grade-scale status row, the real UI trigger for
            take re-measurement (previously only reachable via the idempotent
            take-PATCH workaround). */}
        <RecalibrateTakesButton templateId={templateId} summary={summary} />

        {/* Plain-language coach messages — collapsed behind "Details" (F5,
            single-voice rendering). CoachFeedbackList above is the primary
            voice; these legacy messages still exist for content it doesn't
            cover, but no longer render inline by default. */}
        {summary.coach_messages.length > 0 && (
          <CollapsibleSection
            title="Details"
            variant="default"
            defaultExpanded={false}
            badge={summary.coach_messages.length}
          >
            <ul className="space-y-1.5">
              {summary.coach_messages.map((msg, i) => (
                <li
                  key={i}
                  className="text-sm text-text-secondary flex items-start gap-1.5"
                >
                  <span className="mt-0.5 flex-shrink-0 text-text-muted">&#x2022;</span>
                  <span>{msg}</span>
                </li>
              ))}
            </ul>
          </CollapsibleSection>
        )}

        {/* Gate outcomes brief */}
        {summary.gate_outcomes_brief.length > 0 && (
          <div>
            <p className="text-xs font-medium text-text-muted uppercase tracking-wide mb-1.5">
              Dimension checks
            </p>
            <ul className="space-y-1">
              {summary.gate_outcomes_brief.map((outcome, i) => {
                const isViewLimited =
                  outcome.toLowerCase().includes('view-limited') ||
                  outcome.toLowerCase().includes('not validated');
                return (
                  <li
                    key={i}
                    className={`text-xs flex items-center gap-1.5 ${
                      isViewLimited ? 'text-warning' : 'text-success'
                    }`}
                  >
                    <span>{isViewLimited ? '~' : '✓'}</span>
                    <span>{outcome}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>

      {/* n=2 calibration quality note (owner decision: always surface at n=2).
          HONEST copy: this is a count-based flag, NOT identity detection.
          The backend sets same_person_warning when n_good_takes === 2 exactly.
          Suppressed when coach_feedback already covers this topic (F5,
          single-voice rendering) — see coversSamePersonTopic above. */}
      {summary.same_person_warning && !coversSamePersonTopic(summary) && (
        <Alert variant="info">
          Only 2 correct takes so far — calibration is using generous tolerances. Add a
          3rd correct take (ideally a different athlete, or a visibly different-quality
          performance) for a tighter, more reliable envelope.
        </Alert>
      )}
    </div>
  );
}
